import { RouteComponentProps } from '@reach/router';
import { LessonsApi, SchemeOfWork } from 'api/lessonsApi';
import LessonDetailTop from 'components/lessons/LessonDetailTop';
import LessonPageStatus from 'components/lessons/LessonPageStatus';
import {
  LessonPrintParts,
  useLessonPrint,
} from 'components/lessons/LessonPrint';
import {
  LessonsRouteParams,
  unitPath,
  useLessonsPage,
} from 'components/lessons/lessonsHelpers';
import SubjectSiteLayout from 'components/subjectSite/SubjectSiteLayout';
import React from 'react';

type Props = RouteComponentProps<LessonsRouteParams>;

/**
 * Text with its new lines, and any written as \n, shown as line breaks
 */
const withLineBreaks = (text: string | null) =>
  (text ?? '').split(/\n|\\n/).map((line, index) => (
    <React.Fragment key={index}>
      {index > 0 && <br />}
      {line}
    </React.Fragment>
  ));

const LessonSchemeOfWork: React.FC<Props> = (props) => {
  const result = useLessonsPage<SchemeOfWork>(props, (query) =>
    LessonsApi.getSchemeOfWork(query)
  );
  const subjectsId =
    result && typeof result !== 'string' ? result.subject.subjectsID : null;
  const { print, popup, bError } = useLessonPrint(subjectsId);

  if (result === null || typeof result === 'string') {
    return <LessonPageStatus status={result} />;
  }

  const { subject, query, details: scheme } = result;
  const { activity, keyStageName } = scheme;
  const details = scheme.schemeOfWork;
  const keySkills = details.keyVocabulary?.trim()
    ? details.keyVocabulary.split('\n')
    : [];
  const assessmentYears = Array.from(
    new Set(scheme.assessmentCriterias.map((item) => item.yearNames))
  );

  return (
    <SubjectSiteLayout colorCss={activity.colorCSS}>
      <div className="LessonDetail">
        <LessonPrintParts schoolName={scheme.schoolName} />
        <LessonDetailTop
          activity={activity}
          keyStageName={keyStageName}
          backPath={`${unitPath(
            subject.internalCode,
            scheme.keyStage.strippedName,
            activity.strippedName
          )}?years=${query.years}`}
          lessonLine="Unit of Work"
          onPrint={print}
          printError={
            bError && (
              <span className="ValidationInfo">
                An error occurred, please try again.
              </span>
            )
          }
        />

        <div className="Panel_Cms Panel_Introduction" id="Panel_One">
          <div className="Inner">
            <div className="WidthContent">
              <h2 className="Smalltitle">Introduction</h2>
              <p>{withLineBreaks(details.introduction)}</p>
            </div>
          </div>
        </div>

        <div className="Panel_Cms Panel_KeyVocabulary">
          <div className="Inner">
            <div className="WidthContent">
              <h2 className="Smalltitle">Key Skills</h2>
              <div className="SubLine"></div>
              <ul>
                {keySkills.map((skill, index) => (
                  <li key={index}>{skill}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {scheme.lessons.length > 0 && (
          <div className="Panel_Cms Panel_LearningObjective LearningObjPanel">
            <div className="Inner">
              <div className="WidthContent">
                <h2 className="Smalltitle">Learning Objective</h2>
                <table>
                  <tbody>
                    {scheme.lessons.map((lesson) => (
                      <tr key={lesson.lessonsId}>
                        <td className="Tlab">{`LESSON ${lesson.lessonNumber}`}</td>
                        <td>
                          {lesson.learningObjectives.map((item, index) => (
                            <React.Fragment key={index}>
                              {index > 0 && <br />}
                              {item.objective}
                            </React.Fragment>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {assessmentYears.length > 0 && (
          <div className="Panel_Cms Panel_AssessmentCriteria">
            <div className="Inner">
              <div className="WidthContent">
                <h2 className="Smalltitle">Assessment Criteria</h2>
                <div className="SubLine"></div>
                <div className="BottomFix"></div>
                {/* A space before each inline block column sets their gap */}
                {assessmentYears.map((yearNames) => (
                  <React.Fragment key={yearNames}>
                    {' '}
                    <div className="Column">
                      <div className="Tlab">{yearNames}</div>
                      <table>
                        <tbody>
                          {scheme.assessmentCriterias
                            .filter((item) => item.yearNames === yearNames)
                            .map((item) => (
                              <tr key={item.assessmentSuccessCriteriasId}>
                                <td>{item.criteria}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}

        {scheme.nationalCurriculums.length > 0 && (
          <div className="Panel_Cms Panel_NationalCurriculum">
            <div className="Inner">
              <div className="WidthContent">
                <h2 className="Smalltitle">
                  {scheme.keyStage.name.trim().toLowerCase() === 'eyfs'
                    ? 'Links to the Early Learning Goals'
                    : 'Links to the National Curriculum'}
                </h2>
                <div className="SubLine"></div>
                <div className="BottomFix"></div>
                {scheme.nationalCurriculums.map((item) => (
                  <React.Fragment key={item.nationalCurriculumsId}>
                    {' '}
                    <div className="Column">
                      <div className="Tlab">{item.nationalCurriculumArea}</div>
                      {item.nationalCurriculumSubheading && (
                        <div className="SubTlab">
                          {item.nationalCurriculumSubheading}
                        </div>
                      )}
                      <table>
                        <tbody>
                          {item.learningAreas.map((area, index) => (
                            <tr key={index}>
                              <td>{area.specificLearningArea}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}

        {subject.shortName === 'PE' && (
          <div className="Panel_Cms Panel_HealthSafety">
            <div className="Inner">
              <div className="WidthContent">
                <h2 className="Smalltitle">Healthy Participation</h2>
                <div className="SubLine"></div>
                <div className="BottomFix"></div>
                <div className="SmallTxt">
                  {withLineBreaks(details.healthSafety)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {popup}
    </SubjectSiteLayout>
  );
};

export default LessonSchemeOfWork;
